import { Accordion, AccordionItem, Button } from '@carbon/react';
import { CollapsibleFilterPanel } from 'components/collapsible-filter-panel';
import { CheckboxGroupFilter, RangeFilter } from 'components/data-table';
import { useDebouncedChange } from 'components/debounced-input/use-debounced-change';
import type {
  AddressGroupFilters as Filters,
  AddressMatchedOn,
  SubclusterStatus,
} from '../types';
import {
  DEFAULT_ADDRESS_FILTERS,
  activeFilterCount,
  rangeSet,
  statusOptions,
  type Range,
} from './utils';
import styles from '../page.module.scss';

const MATCHED_ON_OPTIONS: { value: AddressMatchedOn; label: string }[] = [
  { value: 'identical', label: 'Identical Text' },
  { value: 'standardized', label: 'Standardized' },
];

// The numeric min/max fields fire per keystroke, so their changes are debounced;
// the status and matched-on checkbox toggles apply immediately.
const RANGE_DEBOUNCE_MS = 300;

interface CountRangeProps {
  id: string;
  label: string;
  value: Range;
  onChange: (value: Range) => void;
}

/**
 * A min/max count filter with its own Clear.
 * @param {CountRangeProps} props The range.
 * @return {JSX.Element} The range.
 */
function CountRange({ id, label, value, onChange }: CountRangeProps) {
  // Echo keystrokes locally and emit once typing settles, so a range reload
  // doesn't fire per digit. Clear is a single action — emit it immediately.
  const [local, emitTyped] = useDebouncedChange<Range>(
    value,
    onChange,
    RANGE_DEBOUNCE_MS
  );
  return (
    <div className={styles.countRange}>
      <RangeFilter
        id={id}
        label={label}
        value={local}
        min={0}
        minPlaceholder="min"
        maxPlaceholder="max"
        onChange={emitTyped}
      />
      <Button
        kind="ghost"
        size="sm"
        className={styles.clearRange}
        disabled={!rangeSet(local)}
        onClick={() => onChange([null, null])}
      >
        Clear
      </Button>
    </div>
  );
}

interface AddressGroupFiltersProps {
  filters: Filters;
  executed: boolean;
  // Emits only the keys that changed; the page merges the patch functionally, so
  // a range emit still pending in the debounce window can't clobber a checkbox
  // (or a Reset) the reviewer changed meanwhile.
  onChange: (patch: Partial<Filters>) => void;
  isOpen: boolean;
}

/**
 * The address run's group filters: status, matched-on and count ranges.
 * @param {AddressGroupFiltersProps} props The filters.
 * @return {JSX.Element} The filter panel.
 */
export function AddressGroupFilters({
  filters,
  executed,
  onChange,
  isOpen,
}: AddressGroupFiltersProps) {
  return (
    <CollapsibleFilterPanel
      filterCount={activeFilterCount(filters)}
      // Reset clones the one default filter set — a key added there survives Reset
      // rather than needing a second hand-kept copy. The clone's fresh range arrays
      // (not the shared default's) carry a new identity that re-syncs each debounced
      // range input, cancelling an emit still pending in its window so the cleared
      // value can't fire a query afterwards.
      onReset={() => onChange(structuredClone(DEFAULT_ADDRESS_FILTERS))}
      isOpen={isOpen}
    >
      <Accordion>
        <AccordionItem title="Status" open>
          <CheckboxGroupFilter
            id="address-group-status"
            value={filters.statuses}
            onChange={(statuses) =>
              onChange({ statuses: statuses as SubclusterStatus[] })
            }
            options={statusOptions(executed)}
          />
        </AccordionItem>
        <AccordionItem title="Matched On" open>
          <CheckboxGroupFilter
            id="address-group-matched-on"
            value={filters.matchedOn}
            onChange={(matchedOn) =>
              onChange({ matchedOn: matchedOn as AddressMatchedOn[] })
            }
            options={MATCHED_ON_OPTIONS}
          />
        </AccordionItem>
        <AccordionItem title="Counts" open>
          <CountRange
            id="address-group-variations"
            label="Variations"
            value={filters.variations}
            onChange={(variations) => onChange({ variations })}
          />
          <CountRange
            id="address-group-docs"
            label="Docs"
            value={filters.docs}
            onChange={(docs) => onChange({ docs })}
          />
        </AccordionItem>
      </Accordion>
    </CollapsibleFilterPanel>
  );
}
